<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import type { PaymentType, CashDrawer } from '../../../types';
import { useAToast, useADialog } from '@Atoms';
import { useSessionStore } from '@Stores';
import { ROLES } from '@AuthModule';
import useCartStore from '../../../store';
import { CART_TYPES } from '../../../enums';
import { CART_STEPS, CUSTOM_OUTCOME_TYPE, PAYMENT_METHODS } from '../../../types';
import {
  CompleteOutcomeService,
  PostCitationPaymentService,
  ProcessStripePaymentService,
} from '../../../services';
import { PAYMENT_OPTIONS } from '../../../constants';
import { useCartSteps } from '../../../composables';
import { UpdatePaymentTypeService } from '../../../services';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const { can } = useSessionStore();
const {
  animals,
  cardFieldError,
  cardHolderName,
  cartData,
  cashDrawerOptions,
  completeOutcomeRoute,
  isAdoption,
  isCitation,
  isDonation,
  isProduct,
  isReturnToOwner,
  loading,
} = storeToRefs(useCartStore());
const { isCardReaderLastFourInvalid, outcomeCompletedCartReset } = useCartStore();
const { shelterProfile } = storeToRefs(useSessionStore());

const { activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { currentStep, previousStep } = useCartSteps();
const { notify, TOAST_TYPES } = useAToast();
const { push } = useRouter();

const TOAST_ERROR = {
  message: 'errors.generics.unknownIssue.message',
  title: 'errors.generics.unknownIssue.title',
  type: TOAST_TYPES.ERROR,
};

const cardReaderLastFour = ref();
const cashDrawer = ref<CashDrawer | null>(null);
const cashDrawerError = ref<boolean | string>(false);
const creditCardErrorMessage = ref({ name: '', creditCard: '' });
const creditCardModel = ref();
const paymentMethod = ref<PaymentType | null>(null);
const showErrorMessage = ref('');

const hasCashDrawerEnabled = computed(() => shelterProfile.value?.cash_drawer);
const isCardReader = computed(() => paymentMethod.value === PAYMENT_METHODS.CARD_READER);
const isCreditCard = computed(() => paymentMethod.value === PAYMENT_METHODS.CREDIT);
const isRetroactivePayment = computed(() => cartData.value?.paid_date);
const isOutcome = computed(() => cartData.value?.cart_type === CART_TYPES.OUTCOME);
const preAdoptFlags = computed(() => ({
  enablePrePaymentForRtos:
    isReturnToOwner.value && shelterProfile.value?.enable_pre_payment_for_rtos,
  enablePreAdoptForAdoptions:
    isAdoption.value && shelterProfile.value?.enable_pre_adopt_for_adoptions,
}));
const showPrePaymentOption = computed(
  () =>
    isOutcome.value &&
    (preAdoptFlags.value.enablePreAdoptForAdoptions || preAdoptFlags.value.enablePrePaymentForRtos)
);
const shelterHasOffsiteAdoption = computed(
  () =>
    shelterProfile.value?.has_offsite_adoption &&
    can(ROLES.OFFSITE_ANIMAL) &&
    isAdoption.value &&
    !isRetroactivePayment.value
);
const disableSubmitButton = computed(
  () =>
    !!showErrorMessage.value ||
    (!cardReaderLastFour.value && isCardReader.value) ||
    !paymentMethod.value
);
const disablePrePaymentButton = computed(
  () => !showPrePaymentOption.value || disableSubmitButton.value
);
const cartItemType = computed(() =>
  //prettier-ignore
  isCitation.value ? 'citation' :
  isProduct.value ? 'product' :
  isDonation.value ? 'donation' : 
  undefined
);
const options = computed(() =>
  PAYMENT_OPTIONS.map((option) => {
    const disabledForEmptyTotal =
      option.value === PAYMENT_METHODS.CREDIT && !parseFloat(cartData.value?.total);
    const disableCreditCard =
      option.value === PAYMENT_METHODS.CREDIT && !shelterProfile.value?.stripe;

    const message = disableCreditCard
      ? 'shelterInfo.settings.stripe.notIntegrated'
      : 'cart.title.noCreditCardPaymentAvailable';

    return {
      ...option,
      disabled: disabledForEmptyTotal || disableCreditCard,
      disabledMessage: message,
    };
  })
);

const preAdoptLabel = computed(() =>
  // prettier-ignore
  !isOutcome.value ? ''
  :isAdoption.value ? 'cart.label.preAdopt'
  : isReturnToOwner.value ? 'cart.label.prePayment'
  : ''
);

onMounted(() => (currentStep.value = CART_STEPS.PAYMENT_METHOD));

watch(paymentMethod, () => updatePaymentType());
watch(cardHolderName, () => onClearNameError());

// HELPERS
function cleanCcErros() {
  if (creditCardErrorMessage.value?.creditCard) creditCardErrorMessage.value.creditCard = '';
  showErrorMessage.value = '';
}

function onClearNameError() {
  if (!cardHolderName.value)
    return (creditCardErrorMessage.value.name = 'components.forms.errors.required');

  if (creditCardErrorMessage.value?.name) creditCardErrorMessage.value.name = '';
}

function isPaymentMethodsDetailsValid() {
  if (!paymentMethod.value) {
    showErrorMessage.value = 'cart.paymentMethod.error';
    return false;
  }

  const notValidCardReadLastFour = isCardReaderLastFourInvalid(
    cardReaderLastFour.value,
    paymentMethod.value
  );
  if (notValidCardReadLastFour) {
    showErrorMessage.value = 'cart.paymentMethod.lastFour';
    return false;
  }

  if (isCreditCard.value) {
    const noCcModel = !creditCardModel.value?.value && !creditCardModel.value?.token;
    const noName = !cardHolderName.value;

    const errors: Record<string, string> = {};

    if (noCcModel) {
      errors.creditCard = 'components.forms.errors.required';
    }
    if (noName) {
      errors.name = 'components.forms.errors.required';
    }

    if (Object.keys(errors).length || cardFieldError.value) {
      creditCardErrorMessage.value.name = errors.name;
      creditCardErrorMessage.value.creditCard = errors.creditCard;
      return false;
    }
  }

  return true;
}

// SERVICES
function updatePaymentType() {
  if (!paymentMethod.value) return;

  showErrorMessage.value = '';

  const PAYLOAD = {
    payment_type: paymentMethod.value,
  };

  UpdatePaymentTypeService(PAYLOAD)
    .then(() => useCartStore().fetchActiveCart())
    .catch(() => notify(TOAST_ERROR));
}

// EVENTS
function onNextStep() {
  if (!cashDrawer.value && hasCashDrawerEnabled.value)
    return (cashDrawerError.value = 'cart.messages.cashDrawerError');
  if (!paymentMethod.value) return (showErrorMessage.value = 'cart.paymentMethod.error');

  const notValidCardReadLastFour = isCardReaderLastFourInvalid(
    cardReaderLastFour.value,
    paymentMethod.value
  );
  if (notValidCardReadLastFour) return (showErrorMessage.value = 'cart.paymentMethod.lastFour');

  if (isCreditCard.value) {
    const noCcModel = !creditCardModel.value?.value && !creditCardModel.value?.token;
    const noName = !cardHolderName.value;

    const errors: Record<string, string> = {};

    if (noCcModel) {
      errors.creditCard = 'components.forms.errors.required';
    }
    if (noName) {
      errors.name = 'components.forms.errors.required';
    }

    if (Object.keys(errors).length || cardFieldError.value) {
      creditCardErrorMessage.value.name = errors.name;
      creditCardErrorMessage.value.creditCard = errors.creditCard;
      return;
    }
  }

  loading.value = true;

  const animalsBaseRtoSanitezed = animals.value.map((animal: any) => {
    const needsAdjustments = animal.base_rto_fee && typeof animal.base_rto_fee === 'object';

    if (!needsAdjustments) return animal;

    return {
      ...animal,
      base_rto_fee: animal.base_rto_fee.amount.toString() || 0,
      outcome: {
        ...animal.outcome,
        fee: animal.base_rto_fee.amount.toString(),
      },
    };
  });

  const payload = {
    ...cartData.value,
    animals: animalsBaseRtoSanitezed,
    payment_type: paymentMethod.value,
    token: isCreditCard.value ? creditCardModel.value?.token : undefined,
    name_on_card: isCreditCard.value ? creditCardModel.value?.name : undefined,
    last_four_digits: isCardReader.value ? cardReaderLastFour.value : undefined,
  };

  if (hasCashDrawerEnabled.value) {
    payload.cash_drawer = cashDrawer.value?.label;
    payload.cash_drawer_id = cashDrawer.value?._id?.$oid;
  }

  // prettier-ignore
  switch(true){
    case(isCitation.value): return onCitationPayment(payload)
    case(isCreditCard.value): return onCreditPayment(payload)
    default: return onCompleteOutcome(payload)
  }
}

function onCitationPayment(payload: any) {
  PostCitationPaymentService(payload)
    .then(() => push(completeOutcomeRoute.value).then(() => outcomeCompletedCartReset()))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}

function onCreditPayment(payload: any) {
  ProcessStripePaymentService(payload)
    .then(() => push(completeOutcomeRoute.value).then(() => outcomeCompletedCartReset()))
    .catch((error) => {
      showErrorMessage.value =
        error?.response?.data?.error || 'cart.toast.defaultError.error.message';
    })
    .finally(() => (loading.value = false));
}

function onCompleteOutcome(payload: any) {
  activeModal.value = SYSTEM_MODALS.COMPLETE_OUTCOME_LOADING_MESSAGE;
  dialogVisibility.value = true;

  CompleteOutcomeService(payload)
    .then(() => push(completeOutcomeRoute.value).then(() => outcomeCompletedCartReset()))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}

function onOffSitePending() {
  console.log('onOffSitePending', cashDrawer.value, hasCashDrawerEnabled.value);
  if (!cashDrawer.value && hasCashDrawerEnabled.value) {
    return (cashDrawerError.value = 'cart.messages.cashDrawerError');
  }

  activeModal.value = SYSTEM_MODALS.CART_PRE_PAYMENT;

  const { cart_id, paid_date } = cartData.value;

  activeModalProps.value = {
    cardReaderLastFour: cardReaderLastFour.value,
    cartId: cart_id.$oid,
    cashDrawer: cashDrawer.value,
    customOutcomeType: CUSTOM_OUTCOME_TYPE.OFFSITE,
    isCompleteOutcome: false,
    isOffsite: true,
    paidDate: paid_date,
    paymentType: paymentMethod.value,
  };

  dialogVisibility.value = true;
}

function onPreAdopt() {
  if (!isPaymentMethodsDetailsValid()) return;

  if (!cashDrawer.value && hasCashDrawerEnabled.value) {
    return (cashDrawerError.value = 'cart.messages.cashDrawerError');
  }

  activeModal.value = SYSTEM_MODALS.CART_PRE_PAYMENT;

  const { cart_id, paid_date } = cartData.value;

  activeModalProps.value = {
    cardReaderLastFour: cardReaderLastFour.value,
    cartId: cart_id.$oid || cart_id,
    cashDrawer: cashDrawer.value,
    customOutcomeType: 'pre_adopt',
    isCompleteOutcome: false,
    isOffsite: false,
    isRto: isReturnToOwner,
    paidDate: paid_date,
    paymentType: paymentMethod.value,
  };

  dialogVisibility.value = true;
}
</script>

<template>
  <div class="flex h-fit flex-col gap-4">
    <main class="flex-1">
      <header class="flex flex-col gap-4">
        <MCashDrawer
          v-if="hasCashDrawerEnabled"
          v-model="cashDrawer"
          :cashDrawerError
          :cashDrawerOptions
          :loading
          @onChange="cashDrawerError = false"
        />

        <div class="flex flex-col gap-1">
          <AFieldLabel
            v-if="hasCashDrawerEnabled"
            required
            :label="$t('cart.stepper.paymentMethod.title')"
          />

          <div class="flex flex-wrap items-center gap-4">
            <AFieldCheckboxButton
              v-model="paymentMethod"
              class="text-nowrap"
              :options
            />
          </div>
        </div>
      </header>

      <div v-if="paymentMethod">
        <Divider
          class="before:border-t-2"
          type="dashed"
        />

        <div class="flex max-w-[500px] flex-col justify-between gap-8">
          <ACartItemCost
            v-if="cartData"
            totalOnly
            :cartData
            :cartItemType
            :item="cartData"
          />

          <div v-if="paymentMethod === PAYMENT_METHODS.CREDIT">
            <MCartStripeCard
              v-model="creditCardModel"
              :creditCardErrorMessage
              @onStripeCardValidated="cleanCcErros"
            />
          </div>

          <div v-if="paymentMethod === PAYMENT_METHODS.CARD_READER">
            <MCartCardReader
              v-model="cardReaderLastFour"
              :error="showErrorMessage"
            />
          </div>
        </div>
      </div>
    </main>

    <footer class="flex-shrink">
      <Message
        v-if="isDonation && paymentMethod === PAYMENT_METHODS.CREDIT"
        pt:text="text-sm"
        severity="info"
        :closable="false"
      >
        {{ $t('cart.label.updatePaidDateForDonation') }}
      </Message>

      <Message
        v-if="showErrorMessage"
        pt:text="text-sm"
        severity="error"
        @close="showErrorMessage = ''"
      >
        {{ $t(showErrorMessage) }}
      </Message>

      <Message
        v-if="cashDrawerError"
        pt:text="text-sm"
        severity="error"
        @close="cashDrawerError = false"
      >
        {{ $t(cashDrawerError as string) }}
      </Message>

      <div class="flex justify-between">
        <Button
          outlined
          class="af-button af-button-success-outlined w-fit"
          icon="pi pi-arrow-left"
          :loading
          :label="$t('common.back')"
          @click="emits('onStepUpdate', previousStep)"
        />

        <div class="flex flex-wrap gap-2">
          <Button
            v-if="shelterHasOffsiteAdoption"
            class="af-button ml-auto w-fit"
            icon="pi pi-clock"
            severity="info"
            :disabled="loading || paymentMethod === PAYMENT_METHODS.CREDIT"
            :loading="loading"
            :label="$t('cart.label.offsite')"
            @click="onOffSitePending"
          />

          <Button
            v-if="showPrePaymentOption"
            class="af-button ml-auto w-fit"
            icon="pi pi-save"
            severity="info"
            :loading
            :disabled="disablePrePaymentButton"
            :label="$t(preAdoptLabel)"
            @click="onPreAdopt"
          />

          <Button
            class="af-button af-button-success ml-auto"
            iconPos="right"
            :loading
            :disabled="disableSubmitButton"
            :label="$t('cart.label.submitAndComplete')"
            @click="onNextStep"
          >
            <template #icon>
              <FaIcon
                class="mr-2"
                :icon="$icons.faPaw"
              />
            </template>
          </Button>
        </div>
      </div>
    </footer>
  </div>
</template>
